import { motion } from "framer-motion";
import { Plus, Flame, Snowflake, ThumbsUp, Coffee } from "lucide-react";
import { useCart } from "../lib/CartContext";
import { menuImages } from "../lib/menuData";

// Category gradients for items without a photo
const CATEGORY_THEME = {
  hayalan: {
    bg: "linear-gradient(135deg, #2C1E16 0%, #1E2EFC 100%)",
    label: "Hayalan",
  },
  arabica: {
    bg: "linear-gradient(135deg, #2C1E16 0%, #5C4D43 100%)",
    label: "Arabica",
  },
  noncoffee: {
    bg: "linear-gradient(135deg, #1E2EFC 0%, #D4AF37 100%)",
    label: "Non-Coffee",
  },
  mocktail: {
    bg: "linear-gradient(135deg, #D4AF37 0%, #1E2EFC 100%)",
    label: "Mocktail",
  },
  tea: {
    bg: "linear-gradient(135deg, #355E3B 0%, #5C4D43 100%)",
    label: "Tea",
  },
  signature: {
    bg: "linear-gradient(135deg, #1E2EFC 0%, #2C1E16 100%)",
    label: "Signature",
  },
};

const formatRp = (k) => `Rp ${k}.000`;

export const MenuItemCard = ({ item, categoryKey, index = 0 }) => {
  const { add } = useCart();
  const theme = CATEGORY_THEME[categoryKey] || CATEGORY_THEME.hayalan;
  const image = menuImages[item.name];
  const hasHotIce = typeof item.hot === "number" || typeof item.ice === "number";
  const kebab = item.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase().replace(/^-|-$/g, "");

  const handleAdd = (variant, price) => {
    add({
      name: item.name,
      variant,
      price,
      category: theme.label,
    });
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.45, delay: Math.min(index * 0.03, 0.4) }}
      data-testid={`menu-card-${kebab}`}
      className="group relative flex flex-col bg-white rounded-3xl overflow-hidden border border-brand-border transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-brand-coffee/10 hover:border-brand-blue/30"
    >
      {/* Image / illustrated header */}
      <div className="relative aspect-[4/3] overflow-hidden">
        {image ? (
          <img
            src={image}
            alt={item.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center relative"
            style={{ background: theme.bg }}
          >
            <Coffee
              className="w-12 h-12 text-white/30"
              strokeWidth={1.2}
            />
            <span className="absolute bottom-3 right-4 font-serif italic text-white/40 text-sm">
              {theme.label}
            </span>
          </div>
        )}
        {item.signature && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-brand-gold text-brand-coffee text-[10px] uppercase tracking-[0.18em] font-medium px-2.5 py-1 rounded-full">
            <ThumbsUp className="w-3 h-3" strokeWidth={2} />
            Signature
          </span>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 p-5 flex flex-col">
        <h3 className="font-serif text-lg md:text-xl leading-tight tracking-tight text-brand-ink">
          {item.name}
        </h3>

        <div className="mt-auto pt-4">
          {hasHotIce ? (
            <div className="flex items-center gap-2">
              {typeof item.hot === "number" && (
                <button
                  type="button"
                  onClick={() => handleAdd("Hot", item.hot)}
                  data-testid={`add-${kebab}-hot`}
                  className="flex-1 inline-flex items-center justify-between gap-2 px-3 py-2.5 rounded-full bg-brand-sand hover:bg-brand-coffee hover:text-brand-cream text-brand-ink text-sm font-medium transition-all group/btn"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Flame className="w-3.5 h-3.5" strokeWidth={1.8} />
                    Hot
                  </span>
                  <span className="font-serif text-base">{item.hot}k</span>
                  <Plus
                    className="w-3.5 h-3.5 opacity-60 group-hover/btn:opacity-100"
                    strokeWidth={2}
                  />
                </button>
              )}
              {typeof item.ice === "number" && (
                <button
                  type="button"
                  onClick={() => handleAdd("Ice", item.ice)}
                  data-testid={`add-${kebab}-ice`}
                  className="flex-1 inline-flex items-center justify-between gap-2 px-3 py-2.5 rounded-full bg-brand-blue/10 hover:bg-brand-blue hover:text-white text-brand-blue text-sm font-medium transition-all group/btn"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Snowflake className="w-3.5 h-3.5" strokeWidth={1.8} />
                    Ice
                  </span>
                  <span className="font-serif text-base">{item.ice}k</span>
                  <Plus
                    className="w-3.5 h-3.5 opacity-60 group-hover/btn:opacity-100"
                    strokeWidth={2}
                  />
                </button>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <span className="font-serif text-2xl text-brand-blue">
                {formatRp(item.price)}
              </span>
              <button
                type="button"
                onClick={() => handleAdd("Reg", item.price)}
                data-testid={`add-${kebab}`}
                className="inline-flex items-center gap-1.5 bg-brand-blue text-white px-4 py-2.5 rounded-full text-sm font-medium hover:bg-blue-800 transition-all hover:-translate-y-0.5"
              >
                <Plus className="w-4 h-4" strokeWidth={2.4} />
                Add
              </button>
            </div>
          )}
        </div>
      </div>
    </motion.article>
  );
};
